/**
 * KAN-755: форма группы товаров: название, описание, товары (поиск по
 * каталогу, чипы). При правке группа загружается целиком. Ошибки 400 по полям.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/catalog/FormField';
import { useProductGroup, useProductGroupMutations } from '@/hooks/useDiscounts';
import {
  groupToForm,
  serverGroupErrors,
  validateGroupForm,
  type FormErrors,
  type GroupFormValues,
} from '@/lib/discountForm';
import { ProductMultiPicker } from './ProductMultiPicker';

interface ProductGroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Группа для правки; null: новая. */
  groupId: string | null;
}

export function ProductGroupFormDialog({ open, onOpenChange, groupId }: ProductGroupFormDialogProps) {
  const groupQuery = useProductGroup(open ? groupId : null);
  const { create, update } = useProductGroupMutations();
  const [values, setValues] = useState<GroupFormValues>(groupToForm(null));
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const initialized = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initialized.current = null;
      return;
    }
    const key = groupId ?? 'new';
    if (initialized.current === key) return;
    if (groupId && !groupQuery.data) return;
    setValues(groupToForm(groupId ? groupQuery.data : null));
    setErrors({});
    setFormError(null);
    initialized.current = key;
  }, [open, groupId, groupQuery.data]);

  const pending = create.isPending || update.isPending;
  const editing = groupId !== null;

  const submit = async (): Promise<void> => {
    const result = validateGroupForm(values);
    setErrors(result.errors);
    setFormError(null);
    if (!result.body) return;
    try {
      if (groupId) await update.mutateAsync({ id: groupId, body: result.body });
      else await create.mutateAsync(result.body);
      onOpenChange(false);
    } catch (err) {
      const server = serverGroupErrors(err);
      setErrors(server.fields);
      setFormError(server.form);
    }
  };

  let body: JSX.Element;
  if (editing && groupQuery.isError) {
    body = <p className="text-sm text-red-700">Не удалось загрузить группу</p>;
  } else if (editing && groupQuery.isLoading) {
    body = <p className="text-sm text-gray-500">Загрузка...</p>;
  } else {
    body = (
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField id="group-name" label="Название *" error={errors.name}>
          <Input
            id="group-name"
            value={values.name}
            maxLength={255}
            autoComplete="off"
            disabled={pending}
            aria-invalid={errors.name ? true : undefined}
            onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
          />
        </FormField>
        <FormField id="group-description" label="Описание" error={errors.description}>
          <Textarea
            id="group-description"
            rows={2}
            value={values.description}
            disabled={pending}
            onChange={(e) => setValues((prev) => ({ ...prev, description: e.target.value }))}
          />
        </FormField>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-900">Товары ({values.products.length})</span>
          <ProductMultiPicker
            testId="group-products"
            value={values.products}
            onChange={(products) => setValues((prev) => ({ ...prev, products }))}
            disabled={pending}
          />
          {errors.products && <p className="text-xs text-red-700">{errors.products}</p>}
        </div>
        {formError && (
          <p className="text-sm text-red-700" role="alert" data-testid="group-form-error">
            {formError}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Отмена
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </form>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="group-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Изменить группу' : 'Новая группа товаров'}</DialogTitle>
          <DialogDescription>Группа объединяет товары, на которые потом можно дать скидку.</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
